// Slice 40 — top-level boot. Branches on /admin/session:
//   needs_bootstrap → SetupScreen
//   anonymous       → LoginScreen
//   authenticated   → Shell (Layout A)
//
// We re-fetch the session after every credential change (bootstrap/login/
// logout) so a stale 401 from a parallel tab can't strand the SPA in an
// inconsistent state.

import { useCallback, useEffect, useState } from "react";
import { Api, type SessionState } from "./api/client";
import { RouterProvider } from "./router/router";
import { ClaimScreen } from "./screens/ClaimScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { SetupScreen } from "./screens/SetupScreen";
import { renderScreen } from "./screens/screens";
import { Shell } from "./shell/Shell";
import { Logo } from "./ui";

type BootState =
  | { kind: "loading" }
  | { kind: "ready"; session: SessionState }
  | { kind: "error"; message: string };

/**
 * Pull a `claim`-token out of the hash before the regular router sees it.
 * `#/claim/<token>` is an out-of-shell flow; anything else returns null
 * and the rest of the routing logic runs as usual.
 */
function parseClaimToken(hash: string): string | null {
  const m = hash.match(/^#\/claim\/([A-Za-z0-9_-]+)$/);
  return m ? (m[1] ?? null) : null;
}

export function App() {
  const [boot, setBoot] = useState<BootState>({ kind: "loading" });
  const [claimToken, setClaimToken] = useState<string | null>(() =>
    parseClaimToken(window.location.hash),
  );

  const refresh = useCallback(async () => {
    const res = await Api.session();
    if (!res.ok) {
      setBoot({ kind: "error", message: res.error.message });
      return;
    }
    setBoot({ kind: "ready", session: res.data });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onHash = () => setClaimToken(parseClaimToken(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Claim flow takes precedence over any session state — even an
  // authenticated user opening their own invite link should land here.
  if (claimToken) {
    return (
      <ClaimScreen
        token={claimToken}
        onClaimed={() => {
          window.location.hash = "#/overview";
          setClaimToken(null);
          void refresh();
        }}
        onCancel={() => {
          window.location.hash = "";
          setClaimToken(null);
        }}
      />
    );
  }

  if (boot.kind === "loading") return <BootSplash />;
  if (boot.kind === "error")
    return <BootError message={boot.message} onRetry={() => void refresh()} />;

  const { session } = boot;

  if (session.state === "needs_bootstrap") {
    return <SetupScreen onBootstrapped={() => void refresh()} />;
  }
  if (session.state === "anonymous") {
    return <LoginScreen onLoggedIn={() => void refresh()} />;
  }
  return (
    <RouterProvider>
      <Shell
        username={session.username}
        role={session.role}
        onSignedOut={() => void refresh()}
        renderScreen={renderScreen}
      />
    </RouterProvider>
  );
}

function BootSplash() {
  return (
    <div
      style={{
        height: "100%",
        display: "grid",
        placeItems: "center",
        background: "var(--hq-bg)",
        color: "var(--hq-text-dim)",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <Logo size={36} />
        <div
          className="hq-mono"
          style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase" }}
        >
          loading session…
        </div>
      </div>
    </div>
  );
}

function BootError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      style={{
        height: "100%",
        display: "grid",
        placeItems: "center",
        background: "var(--hq-bg)",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 420,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          color: "var(--hq-text-muted)",
        }}
      >
        <Logo size={36} />
        <h2 style={{ margin: 0, color: "var(--hq-text)", fontSize: 17, fontWeight: 600 }}>
          Backend unreachable
        </h2>
        <p style={{ margin: 0, fontSize: 12.5 }}>{message}</p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: "8px 14px",
            background: "var(--hq-accent)",
            color: "#04101a",
            border: "none",
            borderRadius: "var(--hq-radius-lg)",
            fontSize: 12.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}
